import { FC, useMemo, useState } from 'react';
import { Table } from '@gravity-ui/uikit';
import { VideoInfo } from '../../types/video/VideoInfo';
import { DeleteVideoDialog } from './components/dialogs/DeleteVideoDialog';
import { ShareVideoDialog } from './components/dialogs/ShareVideoDialog';
import { getTableColumns } from './utils/getTableColumns';
import { EditVideoDialog } from './components/dialogs/EditVideoDialog';
import { VideoStatus } from './components/VideoStatusLabel';
import { AvailableDialogs } from './constants/availableDialogs';
import { ProfilesRecord } from '../Page/types/ProfilesRecord';
import { EmptyVideoListMessage } from './components/EmptyVideoListMessage';
import './styles/VideoList.css';

export type VideoListUpdateAction = 'edit' | 'delete';

type VideoListProps = {
    videos: VideoInfo[];
    onUpdate: (action: VideoListUpdateAction) => void;
    isMobile?: boolean;
    profiles: ProfilesRecord;
    highlightedVideoId?: string | null;
};

// В мобайле скрываем широкие колонки; clipboard_btn переезжает в MoreActions
const COLUMNS_HIDDEN_IN_MOBILE = ['profile', 'stat', 'date', 'clipboard_btn'];

export const VideoList: FC<VideoListProps> = ({
    videos,
    onUpdate,
    isMobile,
    profiles,
    highlightedVideoId,
}) => {
    const [videoInfo, setVideoInfo] = useState<VideoInfo>({
        video_id: '',
        title: '',
        description: '',
        status: VideoStatus.WaitUploading,
        tusd_url: '',
        created_at: '',
        updated_at: '',
        profile: '',
        summarization_enabled: false,
    });
    const [currentOpenedDialog, setCurrentOpenedDialog] = useState<AvailableDialogs>(
        AvailableDialogs.NONE,
    );

    const columns = useMemo(
        () => getTableColumns(profiles, setVideoInfo, setCurrentOpenedDialog, isMobile),
        [profiles, isMobile],
    );
    const mobileColumns = useMemo(
        () => columns.filter((column) => !COLUMNS_HIDDEN_IN_MOBILE.includes(column.id)),
        [columns],
    );

    const activeColumns = isMobile ? mobileColumns : columns;

    return (
        <div className="page-videolist-container">
            <Table
                width={isMobile ? undefined : 'max'}
                verticalAlign="middle"
                columns={activeColumns}
                data={videos}
                // Таблица умеет рендерить компонент в пустом сообщении, но по типам не умеет
                emptyMessage={(<EmptyVideoListMessage />) as any}
                getRowClassNames={(item) => {
                    return item.video_id === highlightedVideoId ? ['video-row-highlight'] : [];
                }}
            />
            <DeleteVideoDialog
                isOpen={currentOpenedDialog === AvailableDialogs.DELETE}
                videoInfo={videoInfo}
                isMobile={isMobile}
                onClose={() => setCurrentOpenedDialog(AvailableDialogs.NONE)}
                onDelete={() => onUpdate('delete')}
            />
            <ShareVideoDialog
                isOpen={currentOpenedDialog === AvailableDialogs.SHARE}
                videoInfo={videoInfo}
                isMobile={isMobile}
                onClose={() => setCurrentOpenedDialog(AvailableDialogs.NONE)}
            />
            <EditVideoDialog
                isOpen={currentOpenedDialog === AvailableDialogs.EDIT}
                videoInfo={videoInfo}
                profiles={profiles}
                isMobile={isMobile}
                onClose={() => setCurrentOpenedDialog(AvailableDialogs.NONE)}
                onEdit={() => onUpdate('edit')}
            />
        </div>
    );
};
